import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { apiService } from '@/services/api'
import { Order } from '@/types'
import { buildEndpoint, isCustomerServiceUser } from '@/utils/apiUtils'
import { selectSelectedTypeId } from '../csFlow/csFlowSlice'
import * as ordersService from '@/services/ordersService'

// Flutter-style Orders Controller - exact match
interface OrdersState {
  // Orders lists
  orders: Order[]
  filteredOrders: Order[]
  
  // Pagination - now managed by PagerController
  currentPage: number
  totalPages: number
  
  // UI States
  isLoading: boolean
  
  // تتبع آخر وقت جلب البيانات
  lastFetchTime: string | null
  
  searchQuery: string
  currentPeriod: string
  selectedDateRange: { startDate: Date | null; endDate: Date | null } | null
  
  // Toggle states
  selectedToggleIndex: number
  
  // Storage for filters persistence
  searchKey: string
  toggleKey: string
  periodKey: string
  dateRangeKey: string
  
  // Order submission state
  selectedOrder: Order | null
  orderSubmissionLoading: boolean
  orderSubmissionError: string | null
}

const initialState: OrdersState = {
  orders: [],
  filteredOrders: [],
  currentPage: 1,
  totalPages: 1,
  isLoading: false,
  lastFetchTime: null,
  searchQuery: '',
  currentPeriod: 'today',
  selectedDateRange: null,
  selectedToggleIndex: 0,
  searchKey: 'orders_search_query',
  toggleKey: 'orders_selected_toggle',
  periodKey: 'orders_selected_period',
  dateRangeKey: 'orders_date_range',
  selectedOrder: null,
  orderSubmissionLoading: false,
  orderSubmissionError: null,
}

// Flutter-style async thunks matching exact controller methods
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params: { filterParams?: Record<string, any>; loadMore?: boolean }, { getState }) => {
    try {
      const state = getState() as RootState
      
      // Get filter parameters from filter controller if not provided
      let filterParams = params.filterParams || {}
      if (Object.keys(filterParams).length === 0 && state.ordersFilter) {
        filterParams = state.ordersFilter.filterParameters
      }

      // إضافة معاملات الصفحات والحالة
      const queryParams = { ...filterParams }

      if (params.loadMore) {
        // For infinite pagination, use the next page
        const nextPage = state.orders.currentPage + 1
        queryParams.page = nextPage
      } else {
        queryParams.page = state.orders.currentPage
      }
      queryParams.per_page = 25 // عدد الطلبات لكل صفحة من الباك إند

      // إضافة معامل البحث إذا كان موجوداً
      if (state.orders.searchQuery.length > 0) {
        queryParams.search = state.orders.searchQuery
      }

      // إضافة معامل حالة الطلب بناءً على التبديل المحدد
      if (state.orders.selectedToggleIndex > 0) {
        switch (state.orders.selectedToggleIndex) {
          case 1: // نشط
            queryParams.statusId = '1,2,3' // نشط = 1,2,3
            break
          case 2: // تم
            queryParams.statusId = '4' // تم = 4
            break
          case 3: // معلق
            queryParams.statusId = '6' // معلق = 6
            break
        }
      }

      // Use buildEndpoint to automatically handle CS vs cashier
      // Note: For CS, the endpoint should be /customerService/customer-orders
      // but buildEndpoint will convert /cashier/cashier-orders to /customerService/cashier-orders
      // We need special handling for this case
      let endpoint: string
      if (isCashierUser(state)) {
        endpoint = buildEndpoint('/cashier/cashier-orders')
      } else {
        // For CS, use customer-orders endpoint
        endpoint = '/customerService/customer-orders'
      }

      const response = await apiService.axios.get(endpoint, {
        params: queryParams
      })

      if (response.status === 200 && response.data) {
        const data = response.data.data
        if (data?.collection) {
          const collection = data.collection

          try {
            const newOrders = collection.map((json: any) => json as Order)

            if (params.loadMore) {
              // Append new orders to existing list for infinite pagination
              return {
                orders: [...state.orders.orders, ...newOrders],
                totalPages: data.meta?.lastPage ?? 1,
                currentPage: state.orders.currentPage + 1,
                loadMore: true
              }
            } else {
              // Replace orders for fresh load
              return {
                orders: newOrders,
                totalPages: data.meta?.lastPage ?? 1,
                currentPage: data.meta?.currentPage ?? 1,
                loadMore: false
              }
            }
          } catch (e) {
            if (!params.loadMore) {
              return {
                orders: [],
                totalPages: 1,
                currentPage: 1,
                loadMore: false
              }
            }
          }

          // استخراج معلومات الصفحات من meta
          if (data.meta) {
            const meta = data.meta
            return {
              orders: params.loadMore ? [...state.orders.orders, ...collection] : collection,
              totalPages: meta.lastPage ?? 1,
              currentPage: params.loadMore ? state.orders.currentPage + 1 : (meta.currentPage ?? 1),
              loadMore: params.loadMore || false
            }
          }
        } else {
          return {
            orders: [],
            totalPages: 1,
            currentPage: 1,
            loadMore: false
          }
        }
      } else {
        return {
          orders: [],
          totalPages: 1,
          currentPage: 1,
          loadMore: false
        }
      }
    } catch (error) {
      return {
        orders: [],
        totalPages: 1,
        currentPage: 1,
        loadMore: false
      }
    }
  }
)

export const refreshOrders = createAsyncThunk(
  'orders/refreshOrders',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState
    
    // Get filter parameters from filter controller if available
    let filterParams: Record<string, any> = {}
    if (state.ordersFilter) {
      filterParams = state.ordersFilter.filterParameters
    }
    
    return dispatch(fetchOrders({ filterParams }))
  }
)

export const refreshOrdersWithReset = createAsyncThunk(
  'orders/refreshOrdersWithReset',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState
    
    // إعادة تعيين الفلاتر قبل تحديث البيانات
    dispatch(resetFiltersForRefresh())
    
    // Get filter parameters from filter controller if available
    let filterParams: Record<string, any> = {}
    if (state.ordersFilter) {
      filterParams = state.ordersFilter.filterParameters
    }
    
    return dispatch(fetchOrders({ filterParams }))
  }
)

export const refreshOrdersAfterPayment = createAsyncThunk(
  'orders/refreshOrdersAfterPayment',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState
    
    // Get filter parameters from filter controller if available
    let filterParams: Record<string, any> = {}
    if (state.ordersFilter) {
      filterParams = state.ordersFilter.filterParameters
    }

    // إضافة معاملات إضافية لضمان ظهور الطلبات الجديدة
    filterParams.include_new_orders = true
    filterParams.refresh_cache = true

    // إعادة تعيين الصفحة إلى الأولى لضمان ظهور الطلبات الجديدة
    dispatch(setCurrentPage(1))

    return dispatch(fetchOrders({ filterParams }))
  }
)

export const loadMoreOrders = createAsyncThunk(
  'orders/loadMoreOrders',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState
    
    if (state.orders.isLoading || !hasMoreOrders(state)) {
      return // Don't load more if already loading or reached end
    }

    // Get filter parameters from filter controller if available
    let filterParams: Record<string, any> = {}
    if (state.ordersFilter) {
      filterParams = state.ordersFilter.filterParameters
    }

    return dispatch(fetchOrders({ filterParams, loadMore: true }))
  }
)

export const submitOrder = createAsyncThunk(
  'orders/submitOrder',
  async (orderData: {
    items: Array<{
      product_id: string | number
      quantity: number
      size_id?: string | number
      additionals?: Array<{
        id: string | number
        quantity: number
      }>
      notes?: string
    }>
    customer_id?: string | number
    order_type: 'dine_in' | 'takeaway' | 'delivery' | 'car_pickup'
    table_id?: string | number
    payment_method: 'cash' | 'card' | 'online' | 'partial'
    discount_code?: string
    notes?: string
    delivery_address?: string
    estimated_delivery_time?: string
  }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      
      // Use buildEndpoint to automatically handle CS vs cashier
      const endpoint = buildEndpoint('/cashier/orders/submit')

      const response = await apiService.axios.post(endpoint, orderData)

      if (response.status === 200 && response.data) {
        return response.data.data
      } else {
        return rejectWithValue('Failed to submit order')
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit order')
    }
  }
)

// Create order using /api/cashier/orders endpoint
export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      
      // Get cartId - priority: csCart.cartInfo (for CS users) > cartItems.cartInfo (for cashiers)
      let cartId: number | string | undefined
      
      // Check if user is CS - use csCart
      if (isCustomerServiceUser()) {
        cartId = state.csCart?.cartInfo?.id
      } else {
        cartId = state.cartItems.cartInfo?.id
      }
      
      // Fallback: try both if first attempt failed
      if (!cartId) {
        cartId = state.csCart?.cartInfo?.id || state.cartItems.cartInfo?.id
      }
      
      if (!cartId) {
        return rejectWithValue('Cart ID is required')
      }

      // Get branchId - priority: csFlow.selectedBranchId > auth.user.branch.id > localStorage > error
      let branchId: number
      if (state.csFlow?.selectedBranchId) {
        // للمستخدمين CS، استخدم selectedBranchId
        branchId = Number(state.csFlow.selectedBranchId)
      } else {
        // للمستخدمين العاديين (cashier)، استخدم branch ID من auth
        const user = state.auth.user
        if (user?.branch?.id) {
          branchId = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
          if (isNaN(branchId) || branchId <= 0) {
            return rejectWithValue('Invalid branch ID from user data')
          }
        } else {
          // Fallback: محاولة الحصول من localStorage
          try {
            const employeeDataStr = localStorage.getItem('employee_data')
            if (employeeDataStr) {
              const employeeData = JSON.parse(employeeDataStr)
              if (employeeData.branch?.id) {
                branchId = typeof employeeData.branch.id === 'number' 
                  ? employeeData.branch.id 
                  : Number(employeeData.branch.id)
                if (isNaN(branchId) || branchId <= 0) {
                  return rejectWithValue('Invalid branch ID from localStorage')
                }
              } else {
                return rejectWithValue('Branch ID is required. Please ensure the user is logged in with branch information.')
              }
            } else {
              return rejectWithValue('Branch ID is required. Please ensure the user is logged in with branch information.')
            }
          } catch (e) {
            return rejectWithValue('Error getting branch ID: ' + (e instanceof Error ? e.message : String(e)))
          }
        }
      }

      // Get creatorId (logged-in user's user_id)
      const user = state.auth.user as any
      let creatorId: number
      if (user?.user_id) {
        creatorId = Number(user.user_id)
      } else if (user?.user?.id) {
        creatorId = Number(user.user.id)
      } else {
        // Fallback to localStorage
        try {
          const userDataStr = localStorage.getItem('user_data')
          if (userDataStr) {
            const userData = JSON.parse(userDataStr)
            if (userData.user_id) {
              creatorId = Number(userData.user_id)
            } else if (userData.user?.id) {
              creatorId = Number(userData.user.id)
            } else {
              return rejectWithValue('Creator ID not found')
            }
          } else {
            return rejectWithValue('Creator ID not found')
          }
        } catch (e) {
          return rejectWithValue('Creator ID not found')
        }
      }

      // Get typeId - for CS users, use csFlow state, otherwise use cart order_type
      let typeId: number
      if (isCustomerServiceUser()) {
        // For CS users, get typeId from csFlow state
        typeId = selectSelectedTypeId(state)
      } else {
        // For cashier users, get typeId from cart order_type
        const orderType = state.cart.order_type || 'dine_in'
        const getTypeId = (orderType: string): number => {
          switch (orderType) {
            case 'delivery': return 1
            case 'takeaway': return 2
            case 'dine_in': return 3
            case 'car_pickup': return 4
            default: return 3
          }
        }
        typeId = getTypeId(orderType)
      }

      // Get payment method (default to 'cash' if not set)
      const paymentMethod = state.cart.payment_method || 'cash'

      // Get userId only if customer exists
      // Priority: state.customer.customerId (from customer selection) > state.cart.customer_id
      const customerId = state.customer?.hasCustomerData && state.customer?.customerId 
        ? state.customer.customerId 
        : state.cart.customer_id
      const userId = customerId ? Number(customerId) : undefined

      // Get discount code from discount state
      const discountCode = state.discount?.hasDiscount ? state.discount.discountCode : undefined

      // Get notes from cart state
      const notes = state.cart.notes || undefined

      // Get addressId for delivery orders (typeId === 1)
      let addressId: number | undefined
      if (typeId === 1) {
        // For CS users, get from csFlow state
        if (isCustomerServiceUser()) {
          addressId = state.csFlow?.selectedAddressId ? Number(state.csFlow.selectedAddressId) : undefined
        } else {
          // For cashier users, get from cart or cartInfo
          addressId = state.cart.address_id 
            ? Number(state.cart.address_id) 
            : (state.cartItems.cartInfo?.address_id ? Number(state.cartItems.cartInfo.address_id) : undefined)

          if (!addressId) {
            try {
              const storedAddressId = localStorage.getItem('selected_address_id')
              if (storedAddressId) {
                addressId = Number(storedAddressId)
              }
            } catch {
              // ignore
            }
          }
        }
      }

      // Get table information if typeId is 3 (dine_in)
      let tableId: number | undefined
      let tablePrice: number | undefined
      let tableTypeId: number | undefined

      if (typeId === 3) {
        // Get tableId from multiple sources (priority: selectedTable > cart.table_id > cartInfo.table_id)
        tableId = state.tables.selectedTable > 0 
          ? state.tables.selectedTable 
          : (state.cart.table_id ? Number(state.cart.table_id) : undefined)
        
        if (!tableId && state.cartItems.cartInfo?.table_id) {
          tableId = Number(state.cartItems.cartInfo.table_id)
        }

        // Get tablePrice from state or cartInfo
        tablePrice = state.tables.tablePrice > 0 
          ? state.tables.tablePrice 
          : (state.cartItems.cartInfo?.table_price ? Number(state.cartItems.cartInfo.table_price) : undefined)

        // Get tableTypeId - try to get from selected table object, otherwise default to 1
        if (tableId && tableId > 0) {
          const selectedTable = state.tables.tables.find(t => Number(t.id) === tableId)
          // Check for type_id in table object (may not be in TypeScript interface but exists in API response)
          tableTypeId = (selectedTable as any)?.type_id 
            ? Number((selectedTable as any).type_id) 
            : (state.cartItems.cartInfo?.table_type_id 
              ? Number(state.cartItems.cartInfo.table_type_id) 
              : 1)
        } else {
          tableTypeId = state.cartItems.cartInfo?.table_type_id 
            ? Number(state.cartItems.cartInfo.table_type_id) 
            : 1 // Default value
        }
      }

      // Get car information if typeId is 4 (car_pickup)
      let carColor: string | undefined
      let carModel: string | undefined
      let carNumber: string | undefined
      let carOption: number | undefined

      if (typeId === 4) {
        // Get car details from csFlow state
        carColor = state.csFlow?.carColor || undefined
        carModel = state.csFlow?.carModel || undefined
        carNumber = state.csFlow?.carNumber || undefined
        
        // Get carOption from branch data (default to 1 if not found)
        try {
          const selectedBranchData = localStorage.getItem('selected_branch_data')
          if (selectedBranchData) {
            const branchData = JSON.parse(selectedBranchData)
            carOption = branchData.carOption ? Number(branchData.carOption) : 1
          } else {
            carOption = 1 // Default value
          }
        } catch (e) {
          carOption = 1 // Default value on error
        }
      }

      // Determine platform based on user type
      const platform = isCustomerServiceUser() ? 'customer_service' : 'cashier'

      // Build payload
      const payload = {
        cartId: Number(cartId),
        branchId: Number(branchId),
        creatorId: Number(creatorId),
        typeId: Number(typeId),
        paymentMethod: paymentMethod,
        platform: platform,
        ...(userId && { userId: userId }),
        ...(discountCode && { coboneCode: discountCode }),
        ...(notes && { notes: notes }),
        // Add addressId when typeId is 1 (delivery)
        ...(typeId === 1 && addressId && addressId > 0 && {
          addressId: Number(addressId)
        }),
        // Add table fields when typeId is 3 (dine_in)
        ...(typeId === 3 && tableId && tableId > 0 && {
          tableId: Number(tableId),
          tablePrice: tablePrice !== undefined ? Number(tablePrice) : 0,
          tableTypeId: tableTypeId !== undefined ? Number(tableTypeId) : 1
        }),
        // Add car fields when typeId is 4 (car_pickup)
        ...(typeId === 4 && {
          carColor: carColor || '',
          carModel: carModel || '',
          carNumber: carNumber || '',
          carOption: carOption !== undefined ? Number(carOption) : 1
        })
      }

      // Build endpoint - will be /customerService/orders for CS users, /cashier/orders for cashiers
      const endpoint = buildEndpoint('/cashier/orders')
      
      // Make API call
      const response = await apiService.axios.post(endpoint, payload)

      if (response.status === 200 && response.data) {
        const responseData = response.data
        
        // Check if the response indicates an error (even with 200 status)
        if (responseData.message === 'error' || responseData.message === 'Error') {
          // Extract error information including descriptiveMessage
          const errorMessage = responseData.message || 'Failed to create order'
          const descriptiveMessage = responseData.descriptiveMessage || errorMessage
          
          // Return error object with both message and descriptiveMessage
          return rejectWithValue({
            message: errorMessage,
            descriptiveMessage: descriptiveMessage,
            errorData: responseData
          })
        }
        
        // Try multiple possible structures for order ID
        let orderId: string | number | undefined
        
        // Strategy 1: Check if data.data exists (nested structure)
        if (responseData.data) {
          // Check if data.data is an object with id
          if (typeof responseData.data === 'object' && responseData.data !== null) {
            orderId = responseData.data.id || 
                     responseData.data.order_id || 
                     responseData.data.orderId ||
                     responseData.data.order?.id ||
                     responseData.data.order?.order_id
          }
          
          // Check if data.data is an array and first item has id
          if (!orderId && Array.isArray(responseData.data) && responseData.data.length > 0) {
            orderId = responseData.data[0].id || 
                     responseData.data[0].order_id || 
                     responseData.data[0].orderId
          }
        }
        
        // Strategy 2: Check root level
        if (!orderId) {
          orderId = responseData.id || 
                   responseData.order_id || 
                   responseData.orderId ||
                   responseData.order?.id ||
                   responseData.order?.order_id
        }
        
        // Strategy 3: Deep search in data object
        if (!orderId && responseData.data && typeof responseData.data === 'object') {
          const dataObj = responseData.data as any
          // Check nested objects
          if (dataObj.order) {
            orderId = dataObj.order.id || dataObj.order.order_id || dataObj.order.orderId
          }
          // Check if data itself has nested id fields
          if (!orderId) {
            orderId = dataObj.id || dataObj.order_id || dataObj.orderId
          }
        }
        
        // Strategy 4: Check if response.data is the order itself
        if (!orderId && typeof responseData === 'object' && responseData !== null) {
          const anyData = responseData as any
          // Try to find id in any nested structure
          if (anyData.data && typeof anyData.data === 'object') {
            // Recursively check for id
            const findId = (obj: any): string | number | undefined => {
              if (!obj || typeof obj !== 'object') return undefined
              if (obj.id) return obj.id
              if (obj.order_id) return obj.order_id
              if (obj.orderId) return obj.orderId
              // Check nested objects
              for (const key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                  const found = findId(obj[key])
                  if (found) return found
                }
              }
              return undefined
            }
            orderId = findId(anyData.data)
          }
        }
        
        if (!orderId) {
          // Try to extract order data anyway and let the confirmation page handle it
          const orderData = responseData.data || responseData
          // If we have order data but no ID, try to use a temporary approach
          // The confirmation page might be able to work with the full order data
          if (orderData && typeof orderData === 'object') {
            // Try one more time to find id in the order data
            const tempId = (orderData as any).id || (orderData as any).order_id || (orderData as any).orderId
            if (tempId) {
              orderId = tempId
            } else {
              return rejectWithValue('Order ID not found in response')
            }
          } else {
            return rejectWithValue('Order ID not found in response')
          }
        }
        
        return { orderId, data: responseData.data || responseData }
      } else {
        return rejectWithValue('Failed to create order')
      }
    } catch (error: any) {
      // Extract error information including descriptiveMessage
      const errorData = error.response?.data || {}
      const errorMessage = errorData.message || 'Failed to create order'
      const descriptiveMessage = errorData.descriptiveMessage || errorMessage
      
      // Return error object with both message and descriptiveMessage
      return rejectWithValue({
        message: errorMessage,
        descriptiveMessage: descriptiveMessage,
        errorData: errorData
      })
    }
  }
)

// Fetch order details using /api/cashier/orders/{orderId}
export const fetchOrderDetails = createAsyncThunk(
  'orders/fetchOrderDetails',
  async (orderId: string | number, { rejectWithValue }) => {
    try {
      // Use ordersService.fetchOrderDetails which properly maps the response
      const orderData = await ordersService.fetchOrderDetails(orderId)
      return orderData
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch order details')
    }
  }
)

// Flutter-style Orders Slice - exact match
const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    // Flutter-style reducers matching exact controller methods
    initializeController: (state) => {
      state.orders = []
      state.filteredOrders = []
      state.totalPages = 1
      state.isLoading = false
      state.searchQuery = ''
      state.selectedToggleIndex = 0
      state.selectedDateRange = null
    },

    loadSavedFilters: (state) => {
      try {
        // تحميل البحث المحفوظ
        const savedSearch = localStorage.getItem(state.searchKey)
        if (savedSearch && savedSearch.length > 0) {
          state.searchQuery = savedSearch
        }

        // تحميل التوجل المحفوظ
        const savedToggle = localStorage.getItem(state.toggleKey)
        if (savedToggle) {
          const toggleIndex = parseInt(savedToggle)
          if (toggleIndex >= 0 && toggleIndex < 4) { // Assuming 4 toggle options
            state.selectedToggleIndex = toggleIndex
          }
        }

        // تحميل الفترة المحفوظة
        const savedPeriod = localStorage.getItem(state.periodKey)
        if (savedPeriod) {
          state.currentPeriod = savedPeriod
        }

        // تحميل نطاق التاريخ المحفوظ
        const savedDateRange = localStorage.getItem(state.dateRangeKey)
        if (savedDateRange && savedDateRange.length > 0) {
          try {
            const dates = savedDateRange.split('|')
            if (dates.length === 2) {
              const startDate = new Date(dates[0])
              const endDate = new Date(dates[1])
              state.selectedDateRange = { startDate, endDate }
            }
          } catch (e) {
            // تجاهل الأخطاء في تحليل التاريخ
          }
        }
      } catch (e) {
        // تجاهل الأخطاء في تحميل الفلاتر
      }
    },

    saveFilters: (state) => {
      try {
        // حفظ البحث
        if (state.searchQuery.length > 0) {
          localStorage.setItem(state.searchKey, state.searchQuery)
        } else {
          localStorage.removeItem(state.searchKey)
        }

        // حفظ التوجل
        localStorage.setItem(state.toggleKey, state.selectedToggleIndex.toString())

        // حفظ الفترة
        localStorage.setItem(state.periodKey, state.currentPeriod)

        // حفظ نطاق التاريخ
        if (state.selectedDateRange) {
          const dateRange = `${state.selectedDateRange.startDate?.toISOString()}|${state.selectedDateRange.endDate?.toISOString()}`
          localStorage.setItem(state.dateRangeKey, dateRange)
        } else {
          localStorage.removeItem(state.dateRangeKey)
        }
      } catch (e) {
        // تجاهل الأخطاء في حفظ الفلاتر
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    onSearch: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload.trim()
    },

    onPageChanged: (state, action: PayloadAction<number>) => {
      const page = action.payload
      if (page > 0 && page <= state.totalPages) {
        state.currentPage = page
      }
    },

    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },

    onPeriodChanged: (state, action: PayloadAction<string>) => {
      state.currentPeriod = action.payload
    },

    applyDateFilter: (state, action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>) => {
      state.selectedDateRange = action.payload
    },

    clearDateFilter: (state) => {
      state.selectedDateRange = null
    },

    setSelectedToggle: (state, action: PayloadAction<number>) => {
      const index = action.payload
      if (index >= 0 && index < 4) { // Assuming 4 toggle options
        state.selectedToggleIndex = index
      }
    },

    applyFiltersWithoutPagination: (state) => {
      try {
        // Get filter controller filters if available
        let filterParams: Record<string, any> = {}
        // Note: In a real implementation, you'd get this from the filter controller state

        // If we have filter parameters, the data is already filtered from API
        // We only need to apply local filters (search, toggle, date)
        if (Object.keys(filterParams).length > 0) {
          state.filteredOrders = state.orders
        } else {
          // No API filters, apply all local filters
          state.filteredOrders = state.orders
        }

        // Apply local filters (search, toggle, date) to the data
        state.filteredOrders = state.filteredOrders.filter((order: Order) => {
          // تطبيق فلاتر البحث
          const matchesSearch =
              state.searchQuery.length === 0 ||
              order.customerName?.toLowerCase().includes(
                state.searchQuery.toLowerCase(),
              ) ||
              order.phoneNumber?.toString().includes(state.searchQuery) ||
              order.id?.toString().includes(state.searchQuery)

          // تطبيق فلاتر الحالة
          let matchesToggle = true
          if (state.selectedToggleIndex > 0) {
            const originalStatus = getOriginalStatusForTranslation(
              order.status,
            )
            switch (state.selectedToggleIndex) {
              case 1: // نشط
                matchesToggle = originalStatus === 'نشط'
                break
              case 2: // تم
                matchesToggle = originalStatus === 'تم'
                break
              case 3: // معلق
                matchesToggle = originalStatus === 'معلق'
                break
            }
          }

          // تطبيق فلاتر التاريخ (دقيق جداً)
          const matchesDate = isOrderMatchingDateFilter(order, state.selectedDateRange)

          return matchesSearch && matchesToggle && matchesDate
        })
      } catch (e) {
        state.filteredOrders = []
      }
    },

    applyFilters: (state) => {
      try {
        if (state.orders.length === 0) {
          state.filteredOrders = []
          state.totalPages = 1
          return
        }

        // Get filter controller filters if available
        let filterParams: Record<string, any> = {}
        // Note: In a real implementation, you'd get this from the filter controller state

        // If we have filter parameters, we don't need to apply local filters
        // because the data is already filtered from the API
        if (Object.keys(filterParams).length > 0) {
          // Apply only local filters (search, toggle, date) to the filtered data
          state.filteredOrders = state.orders

          // تطبيق الفلاتر المحلية على البيانات المفلترة من API
          state.filteredOrders = state.filteredOrders.filter((order: Order) => {
            // Search filter - البحث في اسم العميل، رقم الهاتف، رقم الطلب
            let matchesSearch = true
            if (state.searchQuery.length > 0) {
              const searchLower = state.searchQuery.toLowerCase()
              const matchesName = order.customerName
                  ?.toLowerCase()
                  .includes(searchLower)
              const matchesPhone = order.phoneNumber?.toString().includes(
                state.searchQuery,
              )
              const matchesId = order.id?.toString().includes(state.searchQuery)
              matchesSearch = matchesName || matchesPhone || matchesId
            }

            // Toggle filter - فلتر حالة الطلب
            let matchesToggle = true
            if (state.selectedToggleIndex > 0) {
              const selectedStatus = getToggleLabel(state.selectedToggleIndex)
              const originalStatus = getOriginalStatusForTranslation(
                selectedStatus,
              )

              // مقارنة الحالة مع النص المترجم والنص الأصلي
              matchesToggle =
                  order.status === selectedStatus ||
                  order.status === originalStatus ||
                  order.status.toLowerCase().includes(
                    selectedStatus.toLowerCase(),
                  ) ||
                  order.status.toLowerCase().includes(
                    originalStatus.toLowerCase(),
                  )
            }

            // Date filter - فلتر التاريخ (دقيق جداً)
            const matchesDate = isOrderMatchingDateFilter(order, state.selectedDateRange)

            return matchesSearch && matchesToggle && matchesDate
          })
        } else {
          // تطبيق الفلاتر على البيانات الأصلية
          state.filteredOrders = state.orders.filter((order: Order) => {
            // Search filter - تحسين البحث ليكون أكثر دقة
            let matchesSearch = true
            if (state.searchQuery.length > 0) {
              const searchLower = state.searchQuery.toLowerCase()

              // البحث في اسم العميل
              const matchesName = order.customerName
                  ?.toLowerCase()
                  .includes(searchLower)

              // البحث في رقم الهاتف
              const matchesPhone = order.phoneNumber?.toString().includes(
                state.searchQuery,
              )

              // البحث في رقم الطلب
              const matchesId = order.id?.toString().includes(state.searchQuery)

              matchesSearch = matchesName || matchesPhone || matchesId
            }

            // Toggle filter - تحسين منطق الفلتر ليعمل في كلا اللغتين
            let matchesToggle = true
            if (state.selectedToggleIndex > 0) {
              // إذا كان التوجل المحدد ليس "الكل"
              const selectedStatus = getToggleLabel(state.selectedToggleIndex)

              // مقارنة الحالة مع النص المترجم الحالي والنص الأصلي
              const originalStatus = getOriginalStatusForTranslation(
                selectedStatus,
              )
              matchesToggle =
                  order.status === selectedStatus ||
                  order.status === originalStatus
            }

            // Date filter (دقيق جداً)
            const matchesDate = isOrderMatchingDateFilter(order, state.selectedDateRange)

            return matchesSearch && matchesToggle && matchesDate
          })
        }

        // لا نحتاج لحساب عدد الصفحات محلياً لأنها تأتي من API
        // فقط نتأكد من أن الصفحة الحالية صحيحة
        if (state.currentPage > state.totalPages) {
          state.currentPage = 1
        }
      } catch (e) {
        state.filteredOrders = []
        state.totalPages = 1
      }
    },

    clearAllFilters: (state) => {
      // إعادة تعيين جميع الفلاتر
      state.searchQuery = ''
      state.selectedToggleIndex = 0
      state.selectedDateRange = null
      state.currentPeriod = 'today'

      // مسح الفلاتر من التخزين
      localStorage.removeItem(state.searchKey)
      localStorage.removeItem(state.toggleKey)
      localStorage.removeItem(state.periodKey)
      localStorage.removeItem(state.dateRangeKey)

      // إعادة تعيين الصفحة
      state.currentPage = 1
    },

    resetFiltersForRefresh: (state) => {
      state.searchQuery = ''
      state.selectedToggleIndex = 0
      state.selectedDateRange = null
      state.currentPeriod = 'today'
    },

    addOrder: (state, action: PayloadAction<Order>) => {
      state.orders.push(action.payload)
    },

    removeOrder: (state, action: PayloadAction<string>) => {
      state.orders = state.orders.filter((order) => order.id !== action.payload)
    },

    updateOrder: (state, action: PayloadAction<Order>) => {
      const index = state.orders.findIndex(
        (order) => order.id === action.payload.id,
      )
      if (index !== -1) {
        state.orders[index] = action.payload
      }
    },

    onLanguageChanged: (_state) => {
      // تحديث النصوص المترجمة
      // Note: In a real implementation, you'd update the toggle labels here
    },

    forceRefresh: (_state) => {
      // تحديث البيانات من API بدون إعادة تعيين الفلاتر
      // Note: This would trigger a new fetch in the component
    },

    setSelectedOrder: (state, action: PayloadAction<Order | null>) => {
      state.selectedOrder = action.payload
    },

    clearOrderSubmissionError: (state) => {
      state.orderSubmissionError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.isLoading = false
        state.orders = action.payload?.orders || []
        state.totalPages = action.payload?.totalPages || 1
        state.currentPage = action.payload?.currentPage || 1
        state.lastFetchTime = new Date().toISOString()
      })
      .addCase(fetchOrders.rejected, (state) => {
        state.isLoading = false
        state.orders = []
        state.filteredOrders = []
        state.totalPages = 1
      })
      .addCase(refreshOrders.fulfilled, (_state, _action) => {
        // Refresh completed
      })
      .addCase(refreshOrdersWithReset.fulfilled, (_state, _action) => {
        // Refresh with reset completed
      })
      .addCase(refreshOrdersAfterPayment.fulfilled, (_state, _action) => {
        // Refresh after payment completed
      })
      .addCase(loadMoreOrders.fulfilled, (_state, _action) => {
        // Load more completed
      })
      .addCase(submitOrder.pending, (state) => {
        state.orderSubmissionLoading = true
        state.orderSubmissionError = null
      })
      .addCase(submitOrder.fulfilled, (state, action) => {
        state.orderSubmissionLoading = false
        state.orderSubmissionError = null
        // Add the new order to the orders list
        if (action.payload) {
          state.orders.unshift(action.payload as Order)
        }
      })
      .addCase(submitOrder.rejected, (state, action) => {
        state.orderSubmissionLoading = false
        state.orderSubmissionError = action.payload as string || 'Failed to submit order'
      })
      .addCase(createOrder.pending, (state) => {
        state.orderSubmissionLoading = true
        state.orderSubmissionError = null
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.orderSubmissionLoading = false
        state.orderSubmissionError = null
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.orderSubmissionLoading = false
        state.orderSubmissionError = action.payload as string || 'Failed to create order'
      })
      .addCase(fetchOrderDetails.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.isLoading = false
        state.selectedOrder = action.payload as Order
      })
      .addCase(fetchOrderDetails.rejected, (state) => {
        state.isLoading = false
      })
  },
})

// Flutter-style helper functions - exact match
function isCashierUser(_state: RootState): boolean {
  try {
    const employeeDataStr = localStorage.getItem('employee_data')
    if (!employeeDataStr) return true

    const employeeData = JSON.parse(employeeDataStr)
    const userType = employeeData.user_type ?? employeeData.type ?? ''

    // إذا كان نوع المستخدم كاشير أو لا يحتوي على customer_service
    const isCashier =
        !userType.toString().toLowerCase().includes('cs') &&
        !userType.toString().toLowerCase().includes('customer_service')

    return isCashier
  } catch (e) {
    // في حالة الخطأ، افتراضياً المستخدم كاشير
    return true
  }
}

function hasMoreOrders(state: RootState): boolean {
  // Use the actual current page instead of calculating from orders length
  return state.orders.currentPage < state.orders.totalPages
}

function getOriginalStatusForTranslation(translatedStatus: string): string {
  // إذا كانت اللغة الإنجليزية، نعيد النص العربي المقابل
  const currentLanguage = localStorage.getItem('language') || 'ar'
  if (currentLanguage === 'en') {
    switch (translatedStatus) {
      case 'Active':
        return 'نشط'
      case 'Done':
        return 'تم'
      case 'Pending':
        return 'معلق'
      default:
        return translatedStatus
    }
  }
  // إذا كانت اللغة العربية، نعيد النص الإنجليزي المقابل
  else {
    switch (translatedStatus) {
      case 'نشط':
        return 'Active'
      case 'تم':
        return 'Done'
      case 'معلق':
        return 'Pending'
      default:
        return translatedStatus
    }
  }
}

function getToggleLabel(index: number): string {
  const labels = ['all', 'active_orders', 'completed', 'pending_orders']
  return labels[index] || 'all'
}

function isOrderMatchingDateFilter(order: Order, selectedDateRange: { startDate: Date | null; endDate: Date | null } | null): boolean {
  if (!selectedDateRange) return true
  
  const start = selectedDateRange.startDate
  const end = selectedDateRange.endDate ?? start
  
  if (!start || !end) return true
  
  // Validate date range - start should not be after end
  if (start > end) {
    return false
  }
  
  // Convert order date to start of day for precise comparison
  const orderDate = new Date(order.dateTime || '')
  orderDate.setHours(0, 0, 0, 0)
  
  const startDate = new Date(start || '')
  startDate.setHours(0, 0, 0, 0)
  
  const endDate = new Date(end || '')
  endDate.setHours(0, 0, 0, 0)

  // Precise date matching - include both start and end dates
  return orderDate.getTime() === startDate.getTime() ||
         orderDate.getTime() === endDate.getTime() ||
         (orderDate > startDate && orderDate < endDate)
}

// Flutter-style selectors - exact match
export const selectOrders = (state: RootState) => state.orders.orders
export const selectFilteredOrders = (state: RootState) => state.orders.filteredOrders
export const selectCurrentPage = (state: RootState) => state.orders.currentPage
export const selectTotalPages = (state: RootState) => state.orders.totalPages
export const selectIsLoading = (state: RootState) => state.orders.isLoading
export const selectLastFetchTime = (state: RootState) => state.orders.lastFetchTime
export const selectSearchQuery = (state: RootState) => state.orders.searchQuery
export const selectCurrentPeriod = (state: RootState) => state.orders.currentPeriod
export const selectSelectedDateRange = (state: RootState) => state.orders.selectedDateRange
export const selectSelectedToggleIndex = (state: RootState) => state.orders.selectedToggleIndex
export const selectHasMoreOrders = (state: RootState) => hasMoreOrders(state)
export const selectTotalOrdersCount = (state: RootState) => state.orders.orders.length
export const selectFilteredOrdersCount = (state: RootState) => state.orders.filteredOrders.length

export const {
  initializeController,
  loadSavedFilters,
  saveFilters,
  setLoading,
  onSearch,
  onPageChanged,
  setCurrentPage,
  onPeriodChanged,
  applyDateFilter,
  clearDateFilter,
  setSelectedToggle,
  applyFiltersWithoutPagination,
  applyFilters,
  clearAllFilters,
  resetFiltersForRefresh,
  addOrder,
  removeOrder,
  updateOrder,
  onLanguageChanged,
  forceRefresh,
  setSelectedOrder,
} = ordersSlice.actions

// Export alias for compatibility - removed duplicate submitOrder export

export default ordersSlice.reducer